import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { InView } from 'react-intersection-observer';
import { Redirect, useLocation } from 'react-router-dom';
import App from '../../App';
import useWindowSize from '../../hooks/useWindowSize';
import InventoryClearanceLogo from '../../images/promo-images/inventory-clearance-event-logo.svg';
import * as dealerService from '../../services/dealerService';
import * as homesService from '../../services/homesService';
import { parseInitialPropsContext } from '../../utils';
import { findMetaDescription } from '../../utils/utils';
import HouseSmartMobileNav from '../Shared/HouseSmartMobileNav/HouseSmartMobileNav';
import HouseSmartPartners from '../Shared/HouseSmartPartners/HouseSmartPartners';
import HouseSmartPillar from '../Shared/HouseSmartPillar/HouseSmartPillar';
import HouseSmartStickyNav from '../Shared/HouseSmartStickyNav/HouseSmartStickyNav';
import HouseSmartTop from '../Shared/HouseSmartTop/HouseSmartTop';
import LargeCTA from '../Shared/LargeCTA/LargeCTA';
import { smoothScroll } from '../Shared/SmokeyTransitional/utils/utils';
import XLCTA from '../Shared/XLCTA/XLCTA';
import HouseSmartStyles from './HouseSmart.styled';
import houseSmartPillarContent from './HouseSmartPillarContent';
import useMountTransition from './useMountTransition';
import GoBigGoHomeTabletLogo from '../../images/promo-images/winter-promo/gbgh-logo-tablet.svg';

const HouseSmart = ({ dealer, homes, isLoading }) => {
  const [inView, setInView] = useState(false);
  const [isPageMounted, setIsPageMounted] = useState(false);
  const [loadOnce, setLoadOnce] = useState(false);
  const hasTransitionedIn = useMountTransition(!inView, 1000);

  const [width, height] = useWindowSize();
  const isDesktop = width && width >= 1200;
  const isMobile = width <= 1099;

  const { hash } = useLocation();

  useEffect(() => {
    if (document) {
      const partnerEl = document.getElementsByClassName('scroll-to-partners');
      let scrollToPartnerFunc = function () {
        smoothScroll('#OurPartners', -40);
      };

      for (var i = 0; i < partnerEl.length; i++) {
        partnerEl[i].addEventListener('click', scrollToPartnerFunc, false);
      }
    }
  });

  useEffect(() => {
    if (document && hash !== '') {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
    setTimeout(() => {
      setIsPageMounted(true);
    }, 700);
  }, []);

  useEffect(() => {
    if (inView == true && loadOnce == false) {
      setLoadOnce(true);
    }
  }, [inView]);

  const customPageMetaDescription = findMetaDescription(
    dealer?.websiteMetaDescriptions,
    'housesmart'
  );

  if (dealer?.websiteTemplateDescription === 'BuilderModel') {
    return <Redirect to="/" />;
  }

  return (
    <App dealer={dealer} homes={homes} isLoading={isLoading}>
      <HouseSmartStyles>
        <Helmet>
          <title>HouseSmart | {formatDealerName(dealer?.dealerName)}</title>
          <meta
            name="description"
            content={customPageMetaDescription?.metaDescription}
          />
        </Helmet>
        <InView threshold={0.15} onChange={setInView}>
          <HouseSmartTop
            isClayton={
              dealer?.brandAcronym.toLowerCase() === 'cla' ? true : false
            }
          />
        </InView>
        {isDesktop && isPageMounted && (
          <HouseSmartStickyNav
            inView={inView == false ? true : false}
            hasTransitionedIn={hasTransitionedIn}
            pageHasLoaded={isPageMounted}
          />
        )}
        <div className="sticky-container">
          {houseSmartPillarContent &&
            houseSmartPillarContent.map((item, i) => (
              <React.Fragment key={item.id || i}>
                <HouseSmartPillar
                  isClayton={
                    dealer?.brandAcronym.toLowerCase() === 'cla' ? true : false
                  }
                  headline={item.headline}
                  logo={item.logo}
                  color={item.color}
                  textColor={item.textColor}
                  slides={item.slides}
                  pillarID={item.id}
                />
              </React.Fragment>
            ))}
          <HouseSmartPartners dealer={dealer} />
          {dealer?.isCorporateAdCampaignParticipant &&
            dealer?.websiteTemplateDescription !== 'NoSpecialOffers' &&
            dealer?.websiteTemplateDescription !==
              'NoSpecialOffersNoEnergySmart' && (
              <LargeCTA
                key="large-cta"
                buttonText="View Offer"
                buttonURL="/special-offers/go-big-go-home"
                backgroundColor="go-big-go-home"
                linkState={(dealer, homes)}
                smallText={'GO BIG & GO HOME SALE'}
                largeText={'These prices are a BIG deal!'}
                headlineWidth={'287px'}
                backgroundImage={
                  !isMobile
                    ? {
                        source: require('../../images/promo-images/go-big-go-home-large-cta.jpg'),
                        srcSet: [
                          {
                            source: require('../../images/webp/go-big-go-home-large-cta.webp'),
                          },
                        ],
                        alt: 'Go Big & Go Home Sale Banner Image',
                      }
                    : {
                        source: require('../../images/promo-images/go-big-go-home-large-cta-mobile.jpg'),
                      }
                }
                absoluteLogo={GoBigGoHomeTabletLogo}
                backgroundPosition={'top center'}
                gaClass="ga-sale-homes-seasonal-campaign"
                dealer={dealer}
                homes={homes}
                logoText={''}
                retailEventLabel={'housesmart_banner_campaign'}
              />
            )}
          <div className="spacer"></div>
          <XLCTA
            dealer={dealer}
            homes={homes}
            largeText="Find Your Perfect Home"
            buttons={[
              {
                buttonText: 'Get More Information',
                buttonURL: '/contact-us',
                gaClass: 'ga-housesmart-get-more-information',
                openForm: 'requestInfo',
                retailEventLabel: 'footer_dual_cta_request',
              },
              {
                buttonText: 'Schedule a Visit',
                buttonURL: '/contact-us',
                gaClass: 'ga-housesmart-schedule-a-visit',
                openForm: 'scheduleVisit',
                retailEventLabel: 'footer_dual_cta_schedule',
              },
            ]}
          />
          {!isDesktop && isPageMounted && <HouseSmartMobileNav />}
        </div>
      </HouseSmartStyles>
    </App>
  );
};

HouseSmart.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default HouseSmart;
